/* eslint-disable no-template-curly-in-string */
import React from 'react'
import { css } from '@emotion/core'
import { useTranslation } from 'react-i18next'
import { usePageView } from '@fs/zion-analytics'
import Head, { Title } from '@fs/zion-head'
import {
  AppLayout,
  colors,
  Divider,
  LayoutBand,
  List,
  ListItem,
  HeaderBlock,
  Separator,
  Body1,
  Skeleton,
  DataPreview,
} from '@fs/zion-ui'
import sendAnalytics from '../../services/analytics-helper'
import useSurnameIndex, { useSurnameIndexStatus } from '../../services/surname-index-service'
import { getPathFromUrl } from '../../services/url-service'
import NotFoundError from '../errors/NotFoundError'
import ServerError from '../errors/ServerError'

const itemCss = css`
  color: ${colors.blue50};
`

export default function ScriptsPage(): JSX.Element {
  const [t] = useTranslation()
  const dataState = useSurnameIndexStatus()
  const data = useSurnameIndex()
  usePageView({ page_detail: 'Surname Index: Scripts' })

  if (dataState.notFound) return <NotFoundError />

  if (dataState.serverError) return <ServerError />

  if (dataState.error) throw dataState.error // Let ErrorBoundary catch this error

  return (
    <AppLayout fullWidth backgroundColor={colors.gray00}>
      <Title>{t('ancestors.surname-index.meta-data.title')}</Title>
      <Head>
        <meta name="robots" content="noindex, follow" />
      </Head>
      <LayoutBand minHeight={700}>
        <Separator size="lg" />

        <HeaderBlock as="h3" size="sm" heading={t('ancestors.select-script.header')} />
        <Body1>{t('ancestors.select-script.description')}</Body1>
        <Divider />
        <Separator size="xxs" />

        <DataPreview.Loading loading={dataState.loading}>
          <div style={{ marginLeft: 16 }} data-testid="scripts-loading">
            {Array.from({ length: 6 }).map(() => (
              <Skeleton.Body2 key={Math.random()} maxWidth={Math.random() * 25 + 50} />
            ))}
          </div>
        </DataPreview.Loading>

        <DataPreview.Ready loading={dataState.loading}>
          <List>
            {data?.scripts &&
              data.scripts.map((item) => {
                return (
                  <ListItem
                    data-testid="scripts-item"
                    dense
                    primaryText={
                      <span css={itemCss}>{t(`ancestors.language.${item.bucketName}.label`, item.bucketName)}</span>
                    }
                    to={getPathFromUrl(item.url, true)}
                    key={item.bucketName}
                    onClick={() => {
                      sendAnalytics({ link_page: 'Surname Index', link_name: 'Scripts: Language' })
                      window?.scrollTo && window?.scrollTo(0, 0)
                    }}
                  />
                )
              })}
          </List>
        </DataPreview.Ready>
      </LayoutBand>
    </AppLayout>
  )
}
